import React from 'react'
import Nav from '../src/components/Nav'
import { urls } from '../src/utils/config'
import Footer from '../src/components/Footer'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles } from '@material-ui/core/styles'
import Background from '../src/components/Subnav/Background'
import FeaturedCard from '../src/components/Academy/Course/FeaturedCard'
import LessonGrid from '../src/components/Academy/Lesson/LessonGrid'
import CourseGrid from '../src/components/Academy/Course/CourseGrid'
import TopicGrid from '../src/components/Academy/Topic/TopicGrid'
import { Typography, Divider, Chip } from '@material-ui/core'
import HelpSection from '../src/components/Help/HelpSection'
import JoinCommunitySection from '../src/components/Footer/JoinCommunitySection'

import { getAcademyPageContent } from '../src/services/academy'
import { Course as ICourse, Topic as ITopic, Lesson as ILesson } from '../types/academy'
import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import Image from 'next/image'

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 'auto',
    maxWidth: '1200px',
  },
  contentSection: {
    margin: '40px 0',
    padding: '20px',
    [theme.breakpoints.down('xs')]: {
      margin: '40px 0',
    },
    [theme.breakpoints.up('sm')]: {
      margin: '50px 0',
    },
    [theme.breakpoints.up('md')]: {
      margin: '120px 0',
    },
  },
  section: {},
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  divider: {
    margin: '40px 0',
    [theme.breakpoints.up('md')]: {
      margin: '80px 0',
    },
  },
  aboveFold: {
    minHeight: '20vh',
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: -1,
  },
  title: {
    color: 'white',
    textAlign: 'left',
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '48px',
      maxWidth: '100%',
      margin: '0 auto 0 auto',
    },
  },
  subtitle: {
    marginTop: '20px',
    marginBottom: '30px',
    position: 'relative',
    width: 'fit-content',
    '& h5': {
      color: theme.palette.primary.main,
      position: 'relative',
    },
  },
  heroContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '400px',
    [theme.breakpoints.up('sm')]: {
      minHeight: '300px',
    },
  },
  heroTextSection: {
    width: '100%',
    padding: '20px',
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
  },
  heroImageSection: {
    display: 'none',
    width: '40%',
    minHeight: '480px',
    height: 'inherit',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  heroImage: {
    width: '100%',
  },
  purpleText: {
    color: theme.palette.primary.main,
  },
  chip: {
    padding: '4px',
    width: 'fit-content',
    marginBottom: '4px',
  },
}))

interface Props {
  courses: ICourse[]
  topics: ITopic[]
  lessons: ILesson[]
}

function Academy({ courses, topics, lessons }: Props): JSX.Element {
  const classes = useStyles({})
  const { push } = useRouter()
  const getFeaturedCourse = (): ICourse =>
    courses.find((course) => course.slug === 'defi-101') || courses[0]
  const featuredCourse = getFeaturedCourse()
  featuredCourse.lessons.sort((lessonA, lessonB) => lessonA.number - lessonB.number)
  const goToCourses = () => push(urls.courses)

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.home,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters Academy | Learn all about crypto`}
            description={`Cryptotesters is your go-to resource to navigate the crypto universe. We help you find the best cryptocurrency exchanges, wallets and much more.`}
            openGraph={{
              url: `https://${process.env.NEXT_PUBLIC_APP_URL}/academy`,
              title: `Cryptotesters Academy | Learn all about crypto`,
              description: `Cryptotesters is your go-to resource to navigate the crypto universe. We help you find the best cryptocurrency exchanges, wallets and much more.`,
              images: [
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/large/academy.jpg`,
                  width: 1200,
                  height: 600,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/small/academy.jpg`,
                  width: 600,
                  height: 300,
                  alt: `Cryptotesters.com`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <Nav />
          <div className={classes.root}>
            <div className={classes.aboveFold}>
              <Background size="xl" />
              <div className={classes.heroContent}>
                <div className={classes.heroTextSection}>
                  <Typography variant="h1" className={classes.title}>
                    Learn all about crypto - <span className={classes.purpleText}>with ease</span>.
                  </Typography>
                  <Typography variant="subtitle2" className={classes.subtitle}>
                    We have compiled a library of video lessons to get you up to speed on all things
                    crypto in no time.
                  </Typography>
                </div>

                <div className={classes.heroImageSection}>
                  <Image
                    layout="fill"
                    objectFit="contain"
                    src={'/academy-hero-image.png'}
                    className={classes.heroImage}
                  />
                </div>
              </div>
            </div>
            <div className={classes.contentSection}>
              {featuredCourse && (
                <div>
                  <Typography variant="h5"> Trending </Typography>
                  <FeaturedCard course={featuredCourse} />
                  <LessonGrid
                    course={featuredCourse}
                    view={'course'}
                    lessons={featuredCourse.lessons}
                  />
                </div>
              )}

              {courses.length > 0 && (
                <div>
                  <Divider className={classes.divider} />
                  <div className={classes.sectionHeader}>
                    <Typography variant="h5"> Courses </Typography>
                    <Chip label="See all Courses" className={classes.chip} onClick={goToCourses} />
                  </div>
                  <CourseGrid courses={courses} />
                </div>
              )}

              {lessons.length > 0 && (
                <div>
                  <Divider className={classes.divider} />
                  <div className={classes.sectionHeader}>
                    <Typography variant="h5"> Lessons </Typography>
                    <Chip label="See all Lessons" className={classes.chip} onClick={goToCourses} />
                  </div>
                  <LessonGrid lessons={lessons} />
                </div>
              )}
              {topics.length > 0 && (
                <div>
                  <Divider className={classes.divider} />
                  <Typography variant="h5"> Topics </Typography>
                  <TopicGrid topics={topics} />
                </div>
              )}
            </div>
          </div>
          <HelpSection />
          <JoinCommunitySection />
          <Footer />
        </>
      )}
    </Amplitude>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  let content: {
    courses: ICourse[]
    topics: ITopic[]
    lessons: ILesson[]
  } = {
    courses: [],
    topics: [],
    lessons: [],
  }
  try {
    content = await getAcademyPageContent()
  } catch (e) {
    console.error(e.message)
  }

  return {
    props: { ...content },
  }
}

export default Academy
